import React, { useState, useEffect } from "react";
import { FaFilter } from "react-icons/fa";
import useTableFilter from "../hooks/useTableFilter";
import colors from "../config/colors";
import numberFormat from "./numberFormat";
import addPrecision from "./addPrecision";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import IconButton from "./IconButton";
import Tooltip from "@mui/material/Tooltip";
import TableSkeliton from "../skelliton/TableSkeliton";
import { ImSortAmountAsc, ImSortAmountDesc } from "react-icons/im";
import { MdDragHandle } from "react-icons/md";

export default function Table({
  header = [],
  data = [],
  countNumber = true,
  SideButtons,
  tableClass,
  noHeader,
  draggable,
  dragEnd,
  countReverse = false,
  tableStyle,
  loading,
  sortable,
  normalTable,
  sideButtonStyle,
  selectIdentifier, // e.g., "id" - the field name to use as unique identifier
  selectedRows = [], // array of selected identifiers from parent
  setSelectedRows, // callback to update parent state
}) {
  const [dtx, setDtx] = useState(data || []);
  const [filter, setFilter] = useState(false);
  const [sort, setSort] = useState(countReverse ? "desc" : "asc");
  const [filterOn, setFilterOn] = useState(false);
  const [reverseCount, setReverseCount] = useState(countReverse);

  // Checkbox selection feature enabled only when selectIdentifier is provided
  const isSelectable = !!selectIdentifier;

  useEffect(() => {
    setDtx(countReverse ? [...data].reverse() : data);
  }, [countReverse, data]);

  const handleDrop = (prop) => {
    if (!Array.isArray(dtx)) return;
    const from = prop.source.index;
    const to = prop.destination.index;
    dtx.splice(to, 0, dtx.splice(from, 1)[0]);
    if (draggable) {
      dragEnd(dtx);
    }
  };

  // Handle individual row selection
  const handleRowSelect = (row) => {
    if (!isSelectable || !setSelectedRows) return;

    const identifier = row[selectIdentifier];
    const isSelected = selectedRows.includes(identifier);

    if (isSelected) {
      setSelectedRows(selectedRows.filter((id) => id !== identifier));
    } else {
      setSelectedRows([...selectedRows, identifier]);
    }
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (!isSelectable || !setSelectedRows || !Array.isArray(dtx)) return;

    const allCurrentIds = dtx.map((row) => row[selectIdentifier]);
    const allSelected = allCurrentIds.every((id) => selectedRows.includes(id));

    if (allSelected) {
      // Deselect all current rows
      setSelectedRows(selectedRows.filter((id) => !allCurrentIds.includes(id)));
    } else {
      // Select all current rows (merge with existing selections)
      const newSelections = [...new Set([...selectedRows, ...allCurrentIds])];
      setSelectedRows(newSelections);
    }
  };

  // Check if all current rows are selected
  const isAllSelected = () => {
    if (!isSelectable || !Array.isArray(dtx) || dtx.length === 0) return false;
    const allCurrentIds = dtx.map((row) => row[selectIdentifier]);
    return allCurrentIds.every((id) => selectedRows.includes(id));
  };

  // Check if some (but not all) rows are selected
  const isIndeterminate = () => {
    if (!isSelectable || !Array.isArray(dtx) || dtx.length === 0) return false;
    const allCurrentIds = dtx.map((row) => row[selectIdentifier]);
    const someSelected = allCurrentIds.some((id) => selectedRows.includes(id));
    const allSelected = allCurrentIds.every((id) => selectedRows.includes(id));
    return someSelected && !allSelected;
  };

  if (loading) return <TableSkeliton />;

  return (
    <DragDropContext onDragEnd={handleDrop}>
      <table
        className={
          `table table-default text-center x-table ${
            !normalTable && "app-table"
          } ` + tableClass
        }
        style={tableStyle}
      >
        <thead>
          <tr>
            {isSelectable && (
              <th style={{ width: "50px" }}>
                <input
                  type="checkbox"
                  checked={isAllSelected()}
                  ref={(el) => {
                    if (el) el.indeterminate = isIndeterminate();
                  }}
                  onChange={handleSelectAll}
                  style={{ cursor: "pointer" }}
                />
              </th>
            )}
            {(countNumber || sortable) && (
              <th className="d-flex" style={{ justifyContent: "center" }}>
                {sortable && (
                  <IconButton
                    icon={
                      sort === "desc" ? (
                        <ImSortAmountDesc color="silver" size={24} />
                      ) : (
                        <ImSortAmountAsc color="silver" size={24} />
                      )
                    }
                    style={{ margin: "10px 0px" }}
                    onClick={() => {
                      setSort(sort === "asc" ? "desc" : "asc");
                      setDtx([...data].reverse());
                      setReverseCount(!reverseCount);
                    }}
                  />
                )}
              </th>
            )}
            {!noHeader &&
              header?.map((hd, ind) => {
                return (
                  <th
                    key={ind.toString()}
                    style={{
                      ...hd.style,
                    }}
                  >
                    {hd.filter && (
                      <React.Fragment>
                        <button
                          style={{ padding: 0, paddingRight: 5 }}
                          className={`btn btn-link`}
                          onClick={() => {
                            if (filterOn === ind) {
                              setDtx(data);
                              return setFilterOn(false);
                            }
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            const f = useTableFilter(
                              {
                                type: hd.filter,
                                data: data,
                                headerName: hd.name,
                              },
                              (result) => {
                                setDtx(result);
                                setFilterOn(ind);
                              },
                              (filterOn) => {
                                setFilterOn(ind);
                                setFilter(filterOn);
                              }
                            );

                            if (!filter) {
                              setFilter({ title: hd.title, component: f });
                            } else setFilter(false);
                          }}
                        >
                          <FaFilter
                            color={
                              filterOn === ind ? "tomato" : colors.veryLightGray
                            }
                          />
                        </button>
                        {hd.title === filter.title && <filter.component />}
                      </React.Fragment>
                    )}
                    {hd.title}
                  </th>
                );
              })}
            {SideButtons && <th></th>}
          </tr>
        </thead>
        <Droppable droppableId="droppable-1">
          {(provided, _) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {Array.isArray(dtx) && dtx.map((row, ind) => {
                const clickable = row.clickable;
                const keys = Object.values(header);
                const isRowSelected =
                  isSelectable && selectedRows.includes(row[selectIdentifier]);

                return (
                  <Draggable
                    key={ind.toString()}
                    draggableId={`draggable-${ind}`}
                    index={ind}
                    isDragDisabled={!draggable}
                  >
                    {(provided, snap) => {
                      return (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            ...row.style,
                            boxShadow: snap.isDragging
                              ? "0px 0px 10px -2px #00000020"
                              : "none",
                            backgroundColor: isRowSelected
                              ? "#f0f8ff"
                              : "transparent",
                          }}
                        >
                          {isSelectable && (
                            <td style={{ width: "50px" }}>
                              <input
                                type="checkbox"
                                checked={isRowSelected}
                                onChange={() => handleRowSelect(row)}
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                          )}
                          {draggable && (
                            <td>
                              <div {...provided.dragHandleProps}>
                                <IconButton icon={<MdDragHandle />} />
                              </div>
                            </td>
                          )}
                          {countNumber && (
                            <td
                              style={{
                                verticalAlign: "middle",
                                gap: "0px 20px",
                              }}
                            >
                              {!reverseCount && ind + 1}
                              {reverseCount && dtx.length - ind}
                            </td>
                          )}
                          {keys.map((ob, x) => {
                            const found = clickable?.cellName === ob.name;
                            return (
                              <td
                                key={x.toString()}
                                className={
                                  (ob.trunecate && `text-truncate`) ||
                                  (ob.numberFormat && `text-right`)
                                }
                                style={{
                                  maxWidth: ob.trunecate ? 200 : "auto",
                                  verticalAlign: "middle",
                                  ...ob.style,
                                }}
                              >
                                {ob.trunecate && (
                                  <Tooltip
                                    title={
                                      <p
                                        style={{
                                          fontSize: 13,
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        {row[ob.name]}
                                      </p>
                                    }
                                  >
                                    <p
                                      className="text-truncate"
                                      style={{ cursor: "pointer", margin: 0 }}
                                    >
                                      {row[ob.name]}
                                    </p>
                                  </Tooltip>
                                )}
                                {!ob.trunecate && (
                                  <React.Fragment>
                                    {found && row[clickable.name] && (
                                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                      <a
                                        style={{ cursor: "pointer" }}
                                        onClick={() => clickable.onClick(row)}
                                        class="text-decoration-none"
                                      >
                                        {row[ob.name]}
                                      </a>
                                    )}
                                    {!(found && row[clickable.name]) &&
                                      (ob.numberFormat
                                        ? addPrecision(
                                            numberFormat(row[ob.name])
                                          )
                                        : row[ob.name] === ""
                                        ? "-"
                                        : row[ob.name])}
                                  </React.Fragment>
                                )}
                              </td>
                            );
                          })}
                          {SideButtons && (
                            <td style={sideButtonStyle}>
                              {SideButtons && (
                                <SideButtons inv={row} ind={ind} />
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    }}
                  </Draggable>
                );
              })}
            </tbody>
          )}
        </Droppable>
      </table>
    </DragDropContext>
  );
}
